"use client";

import { useState, useEffect } from "react";
import CommonSchema from "@/components/common/CommonSchema";
import { motion } from "motion/react";
import {
  Sparkles,
  ArrowRight,
  Tag,
  Gift,
  Calendar,
  CheckCircle2,
  ShieldCheck,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { FadeInSection } from "@/components/common/FadeInSection";
import { Banner } from "@/components/common/Banner";
import { IOffer } from "@/slices/offerSlice";
import { useAppSelector } from "@/store/hooks";
import {
  cleanHeaderTags,
  createLookupObject,
  isValidImgSrc,
} from "@/_helper/helper";

interface OffersClientProps {
  offersData: IOffer[];
  cmsData?: any;
  initialBanner?: any;
}

export default function OffersClient({
  offersData,
  cmsData,
  initialBanner,
}: OffersClientProps) {
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, any>>({});

  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  const banner: any =
    initialBanner ||
    BannerList?.find(
      (b: any) =>
        b?.BannerType?.toLowerCase() === "offers" ||
        b?.BannerType?.toLowerCase() === "offer",
    ) ||
    {};

  const list = offersData || [];

  // Helper to get image source
  const getOfferImage = (offer: IOffer) => {
    if (isValidImgSrc(offer?.Images)) return offer.Images!;
    if (isValidImgSrc(offer?.BannerImage)) return offer.BannerImage!;
    return "/imports/placeholder.png";
  };

  // Helper to clean short description
  const cleanDescription = (htmlStr?: string) => {
    if (!htmlStr) return "";
    const cleaned = htmlStr
      .replace(/\[\s*CHECK AVAILABILITY\s*\]/gi, "")
      .replace(/\[\s*GET CORPORATE QUOTE\s*\]/gi, "")
      .trim();
    return cleanHeaderTags(cleaned);
  };

  // Helper to extract starting price if present in description or property
  const extractPrice = (offer: IOffer) => {
    if (offer?.Price) return offer.Price;
    const str = offer?.ShortDescription || offer?.LongDescription || "";
    const match = str.match(/₹[\d,]+/);
    return match ? match[0] : null;
  };

  return (
    <div className="min-h-screen bg-[#fdfcfa]">
      <CommonSchema />
      {/* Dynamic Banner Component */}
      <Banner
        title={banner?.BannerTitle || "Special Offers & Packages"}
        description={banner?.Description}
        bgImage={banner?.BannerImage}
        backLinkText="Home"
        backLinkUrl="/"
      />

      {/* Main Content Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        {/* Ambient Glow Accents */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#c9a961]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#5a6f3e]/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          {lookUpCMSData?.["Offers - Section 1 : Exclusive Resort Offers"] && (
            <FadeInSection>
              <div
                dangerouslySetInnerHTML={{
                  __html: cleanHeaderTags(
                    lookUpCMSData?.[
                      "Offers - Section 1 : Exclusive Resort Offers"
                    ]?.Template,
                  ),
                }}
              />
            </FadeInSection>
          )}

          {/* <div className="text-center mb-12 md:mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#c9a961]/15 text-[#2a2520] text-xs font-semibold uppercase tracking-widest mb-4">
                <Sparkles className="w-3.5 h-3.5 text-[#c9a961]" />
                CURATED LUXURY EXPERIENCES
              </div>
              <h2
                className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#2a2520] mb-4"
                style={{ fontFamily: "Cormorant Garamond, serif", fontWeight: 400 }}
              >
                Exclusive <span className="italic text-[#c9a961] font-semibold">Resort Offers</span>
              </h2>
              <div className="w-20 h-0.5 bg-gradient-to-r from-transparent via-[#c9a961] to-transparent mx-auto mb-4" />
              <p
                className="text-[#2a2520]/75 text-base md:text-lg max-w-2xl mx-auto"
                style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
              >
                Unlock unforgettable moments with our handpicked dining, staycation, and wellness specials.
              </p>
            </div> */}

          {/* Offers Grid */}
          {list.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-[#2a2520]/10 p-8">
              <Gift className="w-12 h-12 text-[#c9a961] mx-auto mb-4 animate-bounce" />
              <h3
                className="text-2xl text-[#2a2520] mb-2 font-semibold"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                No Offers Available At The Moment
              </h3>
              <p
                className="text-[#2a2520]/70 text-sm"
                style={{ fontFamily: "Jost, sans-serif" }}
              >
                Please check back soon for seasonal packages and exclusive
                dining specials.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
              {list.map((offer, index) => {
                const title = offer.Title || "Exclusive Resort Offer";
                const offerUrl = offer.URL || offer.Url || `offer-${offer.ID}`;
                const targetPath = `/offer/${offerUrl}`;
                const imageSrc = getOfferImage(offer);
                const desc = cleanDescription(
                  offer.ShortDescription || offer.LongDescription,
                );
                const price = extractPrice(offer);

                return (
                  <FadeInSection key={offer.ID || index} delay={index * 0.1}>
                    <motion.div
                      whileHover={{ y: -8 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="bg-white rounded-2xl md:rounded-3xl shadow-lg hover:shadow-2xl border border-[#2a2520]/10 hover:border-[#c9a961]/40 overflow-hidden flex flex-col h-full group transition-all duration-300"
                    >
                      {/* Image Container */}
                      <Link
                        href={targetPath}
                        className="block relative overflow-hidden h-64 sm:h-72 w-full"
                      >
                        <Image
                          src={imageSrc}
                          alt={title}
                          fill
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          className="object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />

                        {/* Top Badge Overlay */}
                        <div className="absolute top-4 left-4 right-4 flex justify-between items-center gap-2">
                          {/* <span
                            className="px-3.5 py-1 bg-black/60 backdrop-blur-md text-white text-xs font-semibold rounded-full border border-white/20 shadow-md flex items-center gap-1.5"
                            style={{ fontFamily: "Jost, sans-serif" }}
                          >
                            <Tag className="w-3 h-3 text-[#c9a961]" />
                            SPECIAL OFFER
                          </span> */}

                          {price && (
                            <span className="px-3 py-1 bg-[#c9a961] text-white text-xs font-bold rounded-full shadow-md">
                              {price}
                            </span>
                          )}
                        </div>
                      </Link>

                      {/* Card Content Body */}
                      <div className="p-6 sm:p-8 flex flex-col flex-1 justify-between">
                        <div>
                          {/* Title */}
                          <Link
                            href={targetPath}
                            className="block group-hover:text-[#c9a961] transition-colors duration-200"
                          >
                            <h3
                              className="text-2xl sm:text-3xl text-[#2a2520] mb-3 leading-tight font-semibold"
                              style={{
                                fontFamily: "Cormorant Garamond, serif",
                              }}
                            >
                              {title}
                            </h3>
                          </Link>

                          {/* Short Description */}
                          {desc && (
                            <div
                              className="text-[#2a2520]/75 text-sm sm:text-base leading-relaxed mb-6 font-light line-clamp-3 prose prose-sm max-w-none"
                              style={{ fontFamily: "Jost, sans-serif" }}
                              dangerouslySetInnerHTML={{ __html: desc }}
                            />
                          )}
                        </div>

                        {/* CTA Footer */}
                        <div className="pt-4 border-t border-[#2a2520]/10 mt-auto flex items-center justify-between gap-4">
                          <Link href={targetPath} className="w-full">
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              className="w-full bg-[#2a2520] hover:bg-[#c9a961] text-white py-3.5 px-6 rounded-full flex items-center justify-center gap-2.5 text-sm font-medium transition-colors duration-300 shadow-sm group-hover:shadow-md cursor-pointer"
                              style={{ fontFamily: "Jost, sans-serif" }}
                            >
                              <span>View Offer Details</span>
                              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </motion.button>
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  </FadeInSection>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
